import { useState } from 'react';

import { expressivo, numero } from '../nucleo/numero.js';
import { rotuloDaProcedencia } from '../nucleo/confianca.js';

/**
 * A memória de cálculo e as premissas — a mesma conta, aberta.
 *
 * O produtor não abre isto. O consultor abre, e é por ele que existe: sem a
 * conta à vista, um estudo de viabilidade não passa por revisão técnica nem
 * por análise de crédito. A alternativa que o setor pratica — um PDF com o
 * resultado e a frase "conforme metodologia própria" — é o que faz cada banco
 * refazer a conta do zero.
 *
 * Fica recolhido, não escondido: o mesmo número para os dois públicos, em
 * profundidades diferentes. Se algum dia a tela do produtor e esta aqui
 * divergirem, o defeito é grave, porque é o defeito que este app nasceu para
 * não repetir.
 */

const NOME_DO_GRUPO = {
  substrato: 'O material',
  gas: 'O gás',
  energia: 'A energia',
  dinheiro: 'O dinheiro',
  comparacao: 'Para comparar',
  precos: 'Preços',
  referencia: 'Referências',
  geral: 'Geral',
};

// A tabela de tradução saiu daqui para `nucleo/confianca.js` quando a lista de
// substratos passou a mostrar a mesma string: duas tabelas para o mesmo enum
// divergem no dia em que alguém acrescenta um valor numa e esquece a outra.

function agrupar(itens) {
  const mapa = new Map();
  for (const i of itens) {
    const g = i.grupo || 'geral';
    if (!mapa.has(g)) mapa.set(g, []);
    mapa.get(g).push(i);
  }
  return [...mapa.entries()];
}

function Passos({ memoria }) {
  return (
    <>
      {agrupar(memoria).map(([grupo, passos]) => (
        <div key={grupo} className="conta__grupo">
          <h4 className="rotulo-secao">{NOME_DO_GRUPO[grupo] ?? grupo}</h4>
          <div className="tabela-rolavel">
            <table className="tabela">
              <thead>
                <tr>
                  <th scope="col">Passo</th>
                  <th scope="col">Como se calcula</th>
                  <th scope="col" className="num">
                    Resultado
                  </th>
                </tr>
              </thead>
              <tbody>
                {passos.map((p) => (
                  <tr key={p.id}>
                    <th scope="row" className="conta__rotulo">
                      {p.rotulo}
                      {p.fonte ? <span className="conta__fonte">{p.fonte}</span> : null}
                    </th>
                    <td>
                      <code className="conta__formula">{p.formula}</code>
                      <code className="conta__substituida">{p.substituida}</code>
                    </td>
                    <td className="num">
                      <strong>{expressivo(p.valor)}</strong>{' '}
                      <span className="terciario">{p.unidade}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

function Premissas({ premissas }) {
  const semValidar = premissas.filter((p) => !p.validada).length;

  return (
    <div className="conta__grupo">
      <h4 className="rotulo-secao">Premissas que entraram nesta conta</h4>
      <p className="terciario conta__nota">
        Só aparecem as premissas que a conta realmente usou.
        {semValidar > 0 ? (
          <>
            {' '}
            {semValidar === 1
              ? 'Uma delas ainda não teve a fonte conferida por uma pessoa'
              : `${semValidar} delas ainda não tiveram a fonte conferida por uma pessoa`}{' '}
            — ter fonte citada e ter fonte verificada são coisas diferentes.
          </>
        ) : null}
      </p>
      <div className="tabela-rolavel">
        <table className="tabela">
          <thead>
            <tr>
              <th scope="col">Premissa</th>
              <th scope="col" className="num">
                Valor
              </th>
              <th scope="col" className="num">
                Faixa usual
              </th>
              <th scope="col">Procedência</th>
            </tr>
          </thead>
          <tbody>
            {premissas.map((p) => (
              <tr key={p.chave}>
                <th scope="row" className="conta__rotulo">
                  {p.rotulo}
                  {p.fonte ? <span className="conta__fonte">{p.fonte}</span> : null}
                  {p.observacao ? (
                    <span className="conta__fonte">{p.observacao}</span>
                  ) : null}
                </th>
                <td className="num">
                  <strong>{expressivo(p.valor)}</strong>{' '}
                  <span className="terciario">{p.unidade}</span>
                </td>
                <td className="num terciario">
                  {p.faixa
                    ? `${numero(p.faixa[0], 2)} – ${numero(p.faixa[1], 2)}`
                    : '—'}
                </td>
                <td>
                  {rotuloDaProcedencia(p.confianca)}
                  {p.validada ? (
                    <span className="conta__conferida" title="fonte conferida por uma pessoa">
                      {' '}
                      ✓ conferida
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function VerAConta({ memoria = [], premissas = [], meta = {} }) {
  const [aberto, setAberto] = useState(false);
  if (!memoria.length) return null;

  return (
    <section className="bloco conta">
      <button
        type="button"
        className="botao botao--secundario botao--largo"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
      >
        {aberto ? 'Fechar a conta' : 'Ver a conta passo a passo'}
      </button>

      {aberto ? (
        <div className="conta__corpo empilha-largo">
          <Passos memoria={memoria} />
          {premissas.length ? <Premissas premissas={premissas} /> : null}
          <p className="terciario">
            Cadeia de cálculo <code>{meta.cadeia}</code> versão{' '}
            <code>{meta.versao}</code>
            {meta.rota_de_calculo ? (
              <>
                , rota <code>{meta.rota_de_calculo}</code>
              </>
            ) : null}
            . Esses identificadores servem para refazer exatamente esta conta
            depois, mesmo que o app mude.
          </p>
        </div>
      ) : null}
    </section>
  );
}
